import React, { useState, useEffect, useCallback, useRef } from 'react';
import {
  Box,
  Typography,
  Paper,
  Chip,
  Skeleton,
  IconButton,
  Tooltip,
  Select,
  MenuItem,
  ListSubheader,
} from '@mui/material';
import InsightsIcon from '@mui/icons-material/Insights';
import MacroCalendar from '../components/MacroCalendar';
import MacroRegimeStrip from '../components/MacroRegimeStrip';
import type { MoverScope } from '../services/api';
import UpcomingEarningsCalendar from '../components/UpcomingEarningsCalendar';
import { TickerLink } from '../components/TickerActionMenu';
import api from '../services/api';
import { getFearGreedIndex, FearGreedData, getSignalsDigest, DigestSignal, getWatchlistItems, WatchlistItemView, getSectorPerformance, SectorPerf } from '../services/api';
import { bloomberg } from '../theme/bloombergTheme';
import { useThemeMode } from '../theme/ThemeContext';
import { useLiveMarket } from '../hooks/useLiveMarket';
import { FlashDir } from '../hooks/useLivePrices';
import { useIsMobile } from '../hooks/useIsMobile';

interface MarketOverviewItem {
  symbol: string;
  name: string;
  price: number;
  chg_1d: number;
  chg_1m: number | null;
  chg_ytd: number | null;
  chg_1y: number | null;
  market_open?: boolean;
}

interface ETFOverviewItem extends MarketOverviewItem {
  category: string;
}

interface CommodityOverviewItem extends MarketOverviewItem {
  category: string;
}

interface TopMover {
  symbol: string;
  company_name: string;
  sector: string;
  country: string;
  price: number;
  change_percent: number;
}

/* ── Sentiment helpers ── */
const sentimentColor = (label: string): string => {
  if (label === 'Extreme Fear') return '#d32f2f';
  if (label === 'Fear') return '#f57c00';
  if (label === 'Neutral') return '#f9a825';
  if (label === 'Greed') return '#388e3c';
  if (label === 'Extreme Greed') return '#1b5e20';
  return '#9e9e9e';
};

// Persistent Fear & Greed card — promoted to sit alongside Top Gainers/Top
// Losers in the Movers row instead of a click-to-expand popover in the
// status strip, so sentiment doesn't need a second, duplicate mention.
const SentimentCard: React.FC = () => {
  const [data, setData] = useState<FearGreedData | null>(null);

  const fetchData = useCallback(async () => {
    try {
      const result = await getFearGreedIndex();
      setData(result);
    } catch (err) {
      console.error('Sentiment fetch error:', err);
    }
  }, []);

  useEffect(() => {
    fetchData();
    const interval = setInterval(fetchData, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, [fetchData]);

  if (!data) {
    return (
      <Paper elevation={2} sx={{ p: 1.5, maxHeight: 268 }}>
        <Skeleton variant="rounded" height={220} />
      </Paper>
    );
  }

  const color = sentimentColor(data.label);
  const score = Math.round(data.score);
  // Semi-circle arc gauge: path length ≈ π·r (r=50) ≈ 157, so score/100 * 157
  // gives the filled-portion dash length — same construction as the mockup.
  const arcLen = (score / 100) * 157;

  return (
    <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: 268 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 0.75, px: 1.25, py: 0.75, borderBottom: `1px solid ${bloomberg.border.primary}`, flexShrink: 0 }}>
        <InsightsIcon sx={{ color, fontSize: 18 }} />
        <Typography sx={{ fontWeight: 700, fontSize: bloomberg.fontSize.bodyDense, color, letterSpacing: '0.03em' }}>Fear &amp; Greed</Typography>
      </Box>
      <Box sx={{ flex: 1, display: 'flex', flexDirection: 'column', alignItems: 'center', justifyContent: 'center', gap: 0, py: 1 }}>
        <Box component="svg" viewBox="0 0 120 62" sx={{ width: 128, height: 66 }}>
          <path d="M10,55 A50,50 0 0,1 110,55" fill="none" stroke={bloomberg.border.secondary} strokeWidth={8} strokeLinecap="round" />
          <path d="M10,55 A50,50 0 0,1 110,55" fill="none" stroke={color} strokeWidth={8} strokeLinecap="round"
            strokeDasharray={`${arcLen} 999`} />
        </Box>
        <Typography sx={{ fontSize: 40, fontWeight: 800, color, lineHeight: 1, mt: -1.5 }}>
          {score}
        </Typography>
        <Typography sx={{ fontWeight: 700, color, textTransform: 'uppercase', letterSpacing: 1, fontSize: bloomberg.fontSize.bodyDense, mt: 0.5 }}>
          {data.label}
        </Typography>
      </Box>
    </Paper>
  );
};

// One-line "Today" synthesis — S&P/Nasdaq/VIX 1D moves (already fetched by
// the parent for the Indexes table, passed in rather than re-fetched),
// today's leading sector (own fetch, same endpoint SectorTicker uses), and
// the next high-impact macro event (own fetch, same endpoint MacroCalendar
// uses). Scoped deliberately to broad-market signal only — no portfolio-
// specific figures, since nothing else on this page is portfolio-scoped.
const TodaySynthesis: React.FC<{ overview: MarketOverviewItem[] }> = ({ overview }) => {
  const [topSector, setTopSector] = useState<SectorPerf | null>(null);
  const [nextEvent, setNextEvent] = useState<{ event: string; date: string; time: string; all_day: boolean } | null>(null);

  useEffect(() => {
    getSectorPerformance()
      .then(r => {
        const sorted = [...(r.sectors || [])].sort((a, b) => b.chg_pct - a.chg_pct);
        setTopSector(sorted[0] || null);
      })
      .catch(() => {});
    api.get('/api/market/macro-calendar')
      .then(({ data }) => {
        // The backend cache returns events from "this week" + "next week" as
        // fetched, with no filtering and no guaranteed order — it can include
        // days that have already passed. Filter to today-or-later and sort by
        // date before picking, otherwise "Next:" can end up showing a stale
        // event from earlier in the week.
        const events: any[] = data.events || [];
        const todayStr = new Date().toISOString().slice(0, 10);
        const upcoming = events
          .filter(e => e.date >= todayStr)
          .sort((a, b) => (a.date + (a.time || '')).localeCompare(b.date + (b.time || '')));
        const next = upcoming.find(e => e.impact === 'high') || upcoming[0];
        setNextEvent(next || null);
      })
      .catch(() => {});
  }, []);

  const find = (sym: string) => overview.find(i => i.symbol === sym);
  const spx = find('^GSPC');
  const ndx = find('^NDX');
  const vix = find('^VIX');
  if (!spx && !ndx && !vix) return null;

  const pctStr = (v: number) => `${v >= 0 ? '+' : ''}${v.toFixed(2)}%`;
  const parts: string[] = [];
  if (spx?.chg_1d != null) parts.push(`S&P ${pctStr(spx.chg_1d)}`);
  if (ndx?.chg_1d != null) parts.push(`Nasdaq ${pctStr(ndx.chg_1d)}`);
  const leadClause = topSector ? `, led by ${topSector.sector}` : '';
  const vixClause = vix?.chg_1d != null ? `; VIX ${vix.chg_1d >= 0 ? 'up' : 'down'} ${Math.abs(vix.chg_1d).toFixed(1)}%` : '';

  let nextClause = '';
  if (nextEvent) {
    const when = nextEvent.all_day ? nextEvent.date : `${nextEvent.date} ${nextEvent.time}`;
    nextClause = ` Next: ${nextEvent.event} (${when}).`;
  }

  return (
    <Paper elevation={2} sx={{ px: 2, py: 1.25, mb: 3, borderLeft: `3px solid ${bloomberg.accent.orange}` }}>
      <Typography sx={{ fontSize: bloomberg.fontSize.body, color: bloomberg.text.primary }}>
        <Box component="span" sx={{ color: bloomberg.accent.orange, fontWeight: 700 }}>Today — </Box>
        {parts.join(', ')}{leadClause}{vixClause}.{nextClause}
      </Typography>
    </Paper>
  );
};

/* ── Simple Market Table ── */
type SortCol = 'name' | 'chg_1d' | 'chg_1m' | 'chg_ytd' | 'chg_1y';

const SimpleMarketTable: React.FC<{
  title: string;
  loading: boolean;
  items: MarketOverviewItem[];
  sortCol: SortCol | null;
  sortDir: 'asc' | 'desc';
  onSort: (c: SortCol) => void;
  maxHeight?: number;
  categories?: string[];
  selectedCategory?: string;
  onCategoryChange?: (cat: string) => void;
  flashes?: Record<string, FlashDir>;
  // Mobile: drop 1M/YTD/1Y down to just Ticker/Name/Price/1D — the fixed
  // 7-column layout has no room to breathe under ~400px.
  compact?: boolean;
}> = ({ title, loading, items, sortCol, sortDir, onSort, maxHeight, categories, selectedCategory, onCategoryChange, flashes, compact }) => {
  if (loading) {
    return (
      <Paper elevation={2} sx={{ p: 2 }}>
        {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={24} sx={{ mb: 1 }} />)}
      </Paper>
    );
  }

  const thBase: React.CSSProperties = { padding: '9px 10px', fontSize: 11, fontWeight: 700, color: bloomberg.accent.orange, textTransform: 'uppercase', letterSpacing: '0.06em', whiteSpace: 'nowrap' };

  const sortBtn = (col: SortCol) => {
    const label = { name: 'Name', chg_1d: '1D', chg_1m: '1M', chg_ytd: 'YTD', chg_1y: '1Y' }[col];
    const active = sortCol === col;
    return (
      <button key={col} type="button" onClick={() => onSort(col)} style={{
        background: 'none', border: 'none', padding: 0, margin: 0,
        font: 'inherit', fontWeight: 700, fontSize: 11,
        color: active ? '#fff' : bloomberg.accent.orange,
        cursor: 'pointer', userSelect: 'none',
        textTransform: 'uppercase', letterSpacing: '0.08em',
      }}>
        {label}{active ? (sortDir === 'desc' ? ' ▼' : ' ▲') : ''}
      </button>
    );
  };

  const perfCell = (val: number | null) => {
    if (val === null || val === undefined) return (
      <td style={{ padding: '8px 10px', fontSize: 12.5, fontWeight: 500, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.muted }}>—</td>
    );
    const isUp = val >= 0;
    const color = isUp ? bloomberg.status.positive : bloomberg.status.negative;
    return (
      <td style={{ padding: '8px 10px', fontSize: 12.5, fontWeight: 600, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', color }}>
        {isUp ? '+' : ''}{val.toFixed(2)}%
      </td>
    );
  };

  return (
    <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: maxHeight || 600 }}>
      <div style={{
        position: 'sticky', top: 0, zIndex: 10, flexShrink: 0,
        background: bloomberg.bg.secondary,
      }}>
        {/* Title row */}
        <div style={{
          display: 'flex', alignItems: 'center', justifyContent: 'space-between',
          padding: '10px 12px 8px',
          borderBottom: `1px solid ${bloomberg.border.primary}`,
        }}>
          <span style={{ fontSize: 14, fontWeight: 700, color: bloomberg.text.primary, letterSpacing: '0.03em' }}>
            {title}
          </span>
          {categories && onCategoryChange && (
            <select
              value={selectedCategory || 'All'}
              onChange={(e) => onCategoryChange(e.target.value)}
              style={{
                background: 'transparent',
                border: `1px solid ${bloomberg.border.primary}`,
                borderRadius: 3,
                color: bloomberg.accent.orange,
                fontSize: 11,
                fontWeight: 700,
                textTransform: 'uppercase',
                letterSpacing: '0.06em',
                cursor: 'pointer',
                outline: 'none',
                padding: '3px 6px',
                fontFamily: 'inherit',
              }}
            >
              {categories.map((cat) => (
                <option key={cat} value={cat} style={{ background: '#1a1a2e', color: '#fff' }}>
                  {cat}
                </option>
              ))}
            </select>
          )}
        </div>
        {/* Column header row */}
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {compact ? (
              <>
                <col style={{ width: '20%' }} />
                <col style={{ width: '38%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '21%' }} />
              </>
            ) : (
              <>
                <col style={{ width: '13%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
              </>
            )}
          </colgroup>
          <thead>
            <tr style={{ borderBottom: `2px solid ${bloomberg.accent.orange}` }}>
              <th style={{ ...thBase, textAlign: 'left', padding: '4px 6px' }}>Ticker</th>
              <th style={{ ...thBase, textAlign: 'left', padding: '4px 8px' }}>{sortBtn('name')}</th>
              <th style={{ ...thBase, textAlign: 'right', padding: '4px 3px' }}>Price</th>
              {(compact ? (['chg_1d'] as const) : (['chg_1d', 'chg_1m', 'chg_ytd', 'chg_1y'] as const)).map((col) => (
                <th key={col} style={{ ...thBase, textAlign: 'right' }}>{sortBtn(col)}</th>
              ))}
            </tr>
          </thead>
        </table>
      </div>
      <div style={{ maxHeight: (maxHeight || 600) - 68, overflow: 'auto' }}>
        <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
          <colgroup>
            {compact ? (
              <>
                <col style={{ width: '20%' }} />
                <col style={{ width: '38%' }} />
                <col style={{ width: '21%' }} />
                <col style={{ width: '21%' }} />
              </>
            ) : (
              <>
                <col style={{ width: '13%' }} />
                <col style={{ width: '22%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
                <col style={{ width: '13%' }} />
              </>
            )}
          </colgroup>
          <tbody key={`${sortCol}-${sortDir}`}>
            {items.map((item) => (
              <tr
                key={item.symbol}
                style={{ borderBottom: `1px solid ${bloomberg.border.primary}`, cursor: 'default' }}
                onMouseEnter={e => (e.currentTarget.style.backgroundColor = bloomberg.bg.hover)}
                onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}
              >
                <td style={{ padding: '8px 10px', fontSize: 12.5, fontWeight: 600, color: bloomberg.accent.orange, fontFamily: '"IBM Plex Mono", monospace', whiteSpace: 'nowrap' }}>
                  <Tooltip
                    title={item.market_open === undefined ? '' : item.market_open ? 'Market open — refreshing live' : 'Market closed — last session price'}
                    placement="top" arrow enterDelay={400}
                  >
                    <span
                      className={item.market_open ? 'bb-live-dot' : undefined}
                      style={{
                        display: 'inline-block', width: 6, height: 6, borderRadius: '50%',
                        marginRight: 6, verticalAlign: 'middle',
                        backgroundColor: item.market_open === undefined
                          ? 'transparent'
                          : item.market_open ? '#22c55e' : '#475569',
                      }}
                    />
                  </Tooltip>
                  {item.symbol}
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12.5, color: bloomberg.text.secondary, maxWidth: 180, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap' as const }}>
                  <Tooltip title={item.name} placement="top" arrow enterDelay={300}>
                    <span>{item.name}</span>
                  </Tooltip>
                </td>
                <td style={{ padding: '8px 10px', fontSize: 12.5, fontWeight: 600, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace' }}>
                  <span
                    key={`${item.symbol}-${flashes?.[item.symbol] || 'none'}-${item.price}`}
                    className={flashes?.[item.symbol] ? `bb-flash-${flashes[item.symbol]}` : undefined}
                    style={{ padding: '1px 3px', display: 'inline-block' }}
                  >
                    {item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 })}
                  </span>
                </td>
                {perfCell(item.chg_1d)}
                {!compact && perfCell(item.chg_1m)}
                {!compact && perfCell(item.chg_ytd)}
                {!compact && perfCell(item.chg_1y)}
              </tr>
            ))}
          </tbody>
        </table>
      </div>
    </Paper>
  );
};

// Expected push cadence (seconds) — mirrors the backend's market-hours gating
// so the countdown estimates the next refresh accurately.
function expectedRefreshSecs(): number {
  const now = new Date();
  const day = now.getUTCDay();      // 0 Sun … 6 Sat
  const hr = now.getUTCHours();
  const active = day >= 1 && day <= 5 && hr >= 0 && hr < 21;
  return active ? 120 : 600;
}

// Countdown to the next live price push.
//
// Anchored to the SERVER's last_updated timestamp (not the browser clock), so
// every connected user sees the SAME countdown synced to the one unified
// server refresh cycle — and a page reload recomputes from the server time
// instead of resetting. last_updated is naive UTC from the backend, hence the
// '+Z' when parsing.
const RefreshCountdown: React.FC<{ lastUpdated: string | null; connected: boolean }> = ({ lastUpdated, connected }) => {
  const [remaining, setRemaining] = useState<number>(0);

  useEffect(() => {
    const tick = () => {
      if (!lastUpdated) { setRemaining(expectedRefreshSecs()); return; }
      const serverMs = new Date(lastUpdated + 'Z').getTime();   // naive UTC -> ms
      const targetMs = serverMs + expectedRefreshSecs() * 1000;
      setRemaining(Math.max(0, Math.ceil((targetMs - Date.now()) / 1000)));
    };
    tick();
    const id = setInterval(tick, 1000);
    return () => clearInterval(id);
  }, [lastUpdated]);

  if (!connected) {
    return (
      <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: bloomberg.text.muted }}>
        reconnecting…
      </span>
    );
  }

  if (remaining <= 0) {
    return (
      <span className="bb-live-dot" style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: bloomberg.accent.orange }}>
        Refreshing now…
      </span>
    );
  }

  const mins = Math.floor(remaining / 60);
  const secs = remaining % 60;
  return (
    <span style={{ fontFamily: '"IBM Plex Mono", monospace', fontSize: 11, color: bloomberg.text.muted }}>
      Refreshing prices in:{' '}
      <span style={{ color: bloomberg.text.secondary, fontWeight: 600 }}>
        {mins}:{secs.toString().padStart(2, '0')}
      </span>
    </span>
  );
};

// Compact index reading for the header status strip.
const IndexMini: React.FC<{ label: string; item?: MarketOverviewItem }> = ({ label, item }) => {
  const chg = item?.chg_1d ?? null;
  const up = (chg ?? 0) >= 0;
  return (
    <Box sx={{ display: 'flex', flexDirection: 'column', minWidth: 66, lineHeight: 1.15 }}>
      <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.08em', color: bloomberg.text.muted }}>{label}</Typography>
      <Box sx={{ display: 'flex', alignItems: 'baseline', gap: 0.5 }}>
        <Typography sx={{ fontSize: 13, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: bloomberg.text.primary }}>
          {item ? item.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }) : '—'}
        </Typography>
        {chg != null && (
          <Typography sx={{ fontSize: 10, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace', color: up ? bloomberg.status.positive : bloomberg.status.negative }}>
            {up ? '+' : ''}{chg.toFixed(2)}%
          </Typography>
        )}
      </Box>
    </Box>
  );
};

// Human labels for the raw signal_type slugs coming from the scanner.
const SIGNAL_LABELS: Record<string, string> = {
  '52w_high': '52-Week High', '52w_low': '52-Week Low',
  'golden_cross': 'Golden Cross', 'death_cross': 'Death Cross',
  'crossed_above_20ma': 'Crossed 20MA ↑', 'crossed_below_20ma': 'Crossed 20MA ↓',
  'crossed_above_50ma': 'Crossed 50MA ↑', 'crossed_below_50ma': 'Crossed 50MA ↓',
  'crossed_above_200ma': 'Crossed 200MA ↑', 'crossed_below_200ma': 'Crossed 200MA ↓',
  'rsi_overbought': 'RSI Overbought', 'rsi_oversold': 'RSI Oversold',
  'momentum_up': 'Momentum ↑', 'momentum_down': 'Momentum ↓',
};
// Country buckets for the signals digest — the majors the user cares about,
// with everything else under "Other". 'UK' also matches GB server-side.
const SIGNAL_COUNTRIES: { value: string; label: string }[] = [
  { value: 'all', label: 'All' },
  { value: 'US', label: 'US' },
  { value: 'UK', label: 'UK' },
  { value: 'DE', label: 'Germany' },
  { value: 'FR', label: 'France' },
  { value: 'JP', label: 'Japan' },
  { value: 'other', label: 'Other' },
];

// Rolling sector-performance ticker — market-cap-weighted daily change per
// (country, sector), fed by every tracked stock. Auto-scrolls like an
// exchange tape; duplicated content gives a seamless loop.
const SectorTicker: React.FC<{ embedded?: boolean }> = ({ embedded }) => {
  const [items, setItems] = useState<SectorPerf[]>([]);
  const { mode } = useThemeMode();
  const labelColor = mode === 'dark' ? '#fff' : '#000';

  useEffect(() => {
    const load = () => getSectorPerformance().then(r => setItems(r.sectors)).catch(() => {});
    load();
    const id = setInterval(load, 5 * 60 * 1000);
    return () => clearInterval(id);
  }, []);

  if (!items.length) return null;

  const chip = (r: SectorPerf, i: number) => {
    const up = r.chg_pct >= 0;
    return (
      <Tooltip key={`${r.country}-${r.sector}-${i}`} title={`1D change · ${r.n} stocks tracked · ${(r.total_cap_usd / 1e9).toFixed(0)}B combined cap`}>
        <Box sx={{
          display: 'inline-flex', alignItems: 'center', gap: 0.75, px: 1.5,
          fontSize: 12, whiteSpace: 'nowrap', flexShrink: 0,
        }}>
          <Typography component="span" sx={{ fontSize: 12, fontWeight: 600, color: labelColor }}>
            {r.country_label} {r.sector}
          </Typography>
          <Typography component="span" sx={{
            fontSize: 12, fontWeight: 800, fontFamily: '"IBM Plex Mono", monospace',
            color: up ? '#22c55e' : '#ef4444',
          }}>
            {up ? '+' : ''}{r.chg_pct.toFixed(2)}%
          </Typography>
        </Box>
      </Tooltip>
    );
  };

  // Duplicate the sequence so the marquee loop has no visible seam.
  const track = [...items, ...items];
  const duration = Math.max(30, items.length * 3.5);

  return (
    <Box sx={{
      display: 'flex', alignItems: 'center', gap: 1, minWidth: 0,
      ...(embedded
        ? { flex: 1, mx: 1 }
        : { mb: 2, py: 0.6, px: 1.5, bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1 }),
    }}>
      <Tooltip title="All sector figures are 1-day (previous close → last price) change">
        <Typography component="span" sx={{
          fontSize: 9, fontWeight: 800, letterSpacing: '0.06em', flexShrink: 0,
          color: bloomberg.text.muted, border: `1px solid ${bloomberg.border.primary}`,
          borderRadius: '3px', px: '5px', py: '1px', cursor: 'default',
        }}>
          1D
        </Typography>
      </Tooltip>
      <Box sx={{
        overflow: 'hidden', position: 'relative', minWidth: 0, flex: 1,
        // fade the edges so chips don't hard-cut against neighboring content
        WebkitMaskImage: 'linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
        maskImage: 'linear-gradient(90deg, transparent 0, #000 24px, #000 calc(100% - 24px), transparent 100%)',
      }}>
      <Box
        className="sector-ticker-track"
        sx={{
          display: 'inline-flex', alignItems: 'center', width: 'max-content',
          animation: `sectorScroll ${duration}s linear infinite`,
          '@keyframes sectorScroll': {
            '0%': { transform: 'translateX(0)' },
            '100%': { transform: 'translateX(-50%)' },
          },
        }}
      >
        {track.map(chip)}
      </Box>
      </Box>
    </Box>
  );
};

// ★ Watchlist strip — your names (manual stars + model portfolios + tracker),
// biggest movers first. The personal layer above market-wide discovery.
const WatchlistStrip: React.FC = () => {
  const [items, setItems] = useState<WatchlistItemView[]>([]);
  useEffect(() => {
    getWatchlistItems().then(setItems).catch(() => {});
  }, []);
  if (!items.length) return null;
  const shown = items.slice(0, 16);
  return (
    <Paper elevation={2} sx={{ p: 1.25, mb: 3 }}>
      <Box sx={{ display: 'flex', gap: 0.75, flexWrap: 'wrap', alignItems: 'center' }}>
        <Typography sx={{ fontSize: 12, fontWeight: 800, color: bloomberg.accent.orange, mr: 0.5 }}>
          ★ WATCHLIST ({items.length})
        </Typography>
        {shown.map(it => (
          <Box key={it.symbol} sx={{
            display: 'inline-flex', alignItems: 'center', gap: 0.5, px: 1, py: '3px',
            borderRadius: '4px', bgcolor: bloomberg.bg.tertiary,
            border: `1px solid ${bloomberg.border.primary}`,
          }}>
            <TickerLink symbol={it.symbol} fontSize={11} />
            {it.chg_1d != null && (
              <span style={{ fontSize: 10, fontWeight: 700, fontFamily: '"IBM Plex Mono", monospace',
                color: it.chg_1d >= 0 ? '#22c55e' : '#ef4444' }}>
                {it.chg_1d >= 0 ? '+' : ''}{it.chg_1d.toFixed(1)}%
              </span>
            )}
          </Box>
        ))}
        {items.length > 16 && (
          <Typography sx={{ fontSize: 10, color: bloomberg.text.muted }}>+{items.length - 16} more</Typography>
        )}
      </Box>
    </Paper>
  );
};

// "What needs action" — curated market-wide discovery digest. The backend
// FX-normalizes market caps to USD, applies a $2B floor, filters by country
// bucket, and ranks by notability. This surfaces real movers, not microcap noise.
const SignalsDigest: React.FC = () => {
  const [signals, setSignals] = useState<DigestSignal[]>([]);
  const [matched, setMatched] = useState(0);
  const [loading, setLoading] = useState(true);
  const [dir, setDir] = useState<'all' | 'bullish' | 'bearish'>('all');
  const [country, setCountry] = useState<string>('all');

  useEffect(() => {
    let alive = true;
    setLoading(true);
    getSignalsDigest({ country, direction: dir, min_cap_usd: 2e9, limit: 12 })
      .then((data) => {
        if (!alive) return;
        setSignals(data.signals || []);
        setMatched(data.total_matched || 0);
      })
      .catch(() => { if (alive) { setSignals([]); setMatched(0); } })
      .finally(() => { if (alive) setLoading(false); });
    return () => { alive = false; };
  }, [country, dir]);

  const pill = (active: boolean, label: string, onClick: () => void) => (
    <Box onClick={onClick} sx={{
      cursor: 'pointer', px: 1, py: '2px', borderRadius: '4px', fontSize: 10, fontWeight: 700,
      letterSpacing: '0.04em', whiteSpace: 'nowrap',
      bgcolor: active ? bloomberg.accent.orange : bloomberg.bg.tertiary,
      color: active ? bloomberg.text.inverse : bloomberg.text.secondary,
      border: `1px solid ${active ? bloomberg.accent.orange : bloomberg.border.primary}`,
    }}>{label}</Box>
  );

  return (
    <Paper elevation={2} sx={{ p: 0, mb: 3, display: 'flex', flexDirection: 'column' }}>
      <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}`, flexWrap: 'wrap' }}>
        <InsightsIcon sx={{ color: bloomberg.accent.orange, fontSize: 20 }} />
        <Typography sx={{ fontWeight: 700, fontSize: 13, color: bloomberg.accent.orange }}>Signals — What's Moving</Typography>
        <Typography sx={{ fontSize: 10, color: bloomberg.text.muted, mr: 1 }}>
          {loading ? '' : `${matched} names ≥ $2B`}
        </Typography>
        <Box sx={{ display: 'flex', gap: 0.5, flexWrap: 'wrap' }}>
          {SIGNAL_COUNTRIES.map(c => pill(country === c.value, c.label, () => setCountry(c.value)))}
        </Box>
        <Box sx={{ ml: 'auto', display: 'flex', gap: 0.5 }}>
          {pill(dir === 'all', 'All', () => setDir('all'))}
          {pill(dir === 'bullish', 'Bull', () => setDir('bullish'))}
          {pill(dir === 'bearish', 'Bear', () => setDir('bearish'))}
        </Box>
      </Box>
      {loading ? (
        <Box sx={{ p: 1.5, display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' }, gap: 1 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={26} />)}
        </Box>
      ) : signals.length === 0 ? (
        <Typography sx={{ p: 2, fontSize: 12, color: bloomberg.text.muted }}>No signals match this filter.</Typography>
      ) : (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr' } }}>
          {signals.map((s, i) => {
            const bull = s.direction === 'bullish';
            const c = bull ? bloomberg.status.positive : bloomberg.status.negative;
            return (
              <Box key={`${s.symbol}-${s.signal_type}-${i}`} sx={{
                display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 0.75,
                borderBottom: `1px solid ${bloomberg.border.primary}`,
                borderRight: { md: i % 2 === 0 ? `1px solid ${bloomberg.border.primary}` : 'none' },
              }}>
                <Box sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: c, flexShrink: 0 }} />
                {s.watched && <span style={{ color: '#ff8c00', fontSize: 11, flexShrink: 0 }}>★</span>}
                <Box sx={{ minWidth: 58, flexShrink: 0 }}><TickerLink symbol={s.symbol} fontSize={12} /></Box>
                <Typography sx={{ fontSize: 11, color: bloomberg.text.secondary, overflow: 'hidden', textOverflow: 'ellipsis', whiteSpace: 'nowrap', flex: 1 }} title={s.detail || undefined}>
                  {s.name || '—'}
                </Typography>
                <Typography sx={{ fontSize: 11, fontWeight: 700, color: c, flexShrink: 0, textAlign: 'right' }}>
                  {SIGNAL_LABELS[s.signal_type] || s.signal_type}
                </Typography>
              </Box>
            );
          })}
        </Box>
      )}
    </Paper>
  );
};

const Dashboard = () => {
  const isMobile = useIsMobile();
  const [overview, setOverview] = useState<MarketOverviewItem[]>([]);
  const [etfOverview, setEtfOverview] = useState<ETFOverviewItem[]>([]);
  const [commodityOverview, setCommodityOverview] = useState<CommodityOverviewItem[]>([]);
  const [loadingOverview, setLoadingOverview] = useState(true);
  const [loadingEtfs, setLoadingEtfs] = useState(true);
  const [loadingCommodities, setLoadingCommodities] = useState(true);
  const [topGainers, setTopGainers] = useState<TopMover[]>([]);
  const [topLosers, setTopLosers] = useState<TopMover[]>([]);
  const [loadingGainers, setLoadingGainers] = useState(true);
  const [loadingLosers, setLoadingLosers] = useState(true);
  const [moverRegion, setMoverRegion] = useState<string>('all');
  // '' = whole region. A country code here narrows the movers further; the API
  // takes region slugs and country codes in the same parameter.
  const [moverCountry, setMoverCountry] = useState<string>('');
  const [moverScopes, setMoverScopes] = useState<MoverScope[]>([]);
  const [marketTab, setMarketTab] = useState<'indexes' | 'etfs' | 'commodities'>('indexes');
  const [indexSortCol, setIndexSortCol] = useState<SortCol | null>(null);
  const [indexSortDir, setIndexSortDir] = useState<'asc' | 'desc'>('desc');
  const [etfSortCol, setEtfSortCol] = useState<SortCol | null>(null);
  const [etfSortDir, setEtfSortDir] = useState<'asc' | 'desc'>('desc');
  const [etfCategory, setEtfCategory] = useState<string>('All');
  const [commoditySortCol, setCommoditySortCol] = useState<SortCol | null>(null);
  const [commoditySortDir, setCommoditySortDir] = useState<'asc' | 'desc'>('desc');
  const [commodityCategory, setCommodityCategory] = useState<string>('All');

  // Live market stream (SSE) — indexes, ETFs, commodities + flash signals.
  const live = useLiveMarket();

  // Once the stream has data it drives the three tables (zero polling).
  useEffect(() => {
    if (live.indexes.length) { setOverview(live.indexes as any); setLoadingOverview(false); }
  }, [live.indexes]);
  useEffect(() => {
    if (live.etfs.length) { setEtfOverview(live.etfs as any); setLoadingEtfs(false); }
  }, [live.etfs]);
  useEffect(() => {
    if (live.commodities.length) { setCommodityOverview(live.commodities as any); setLoadingCommodities(false); }
  }, [live.commodities]);

  const fetchOverview = async () => {
    setLoadingOverview(true);
    try {
      const { data } = await api.get(`/api/market/index-overview`);
      setOverview(data);
    } catch (err) {
      console.error('Error fetching index overview:', err);
    } finally {
      setLoadingOverview(false);
    }
  };

  const fetchEtfOverview = async () => {
    setLoadingEtfs(true);
    try {
      const { data } = await api.get(`/api/market/etf-overview`);
      setEtfOverview(data);
    } catch (err) {
      console.error('Error fetching ETF overview:', err);
    } finally {
      setLoadingEtfs(false);
    }
  };

  const fetchCommodityOverview = async () => {
    setLoadingCommodities(true);
    try {
      const { data } = await api.get(`/api/market/commodity-overview`);
      setCommodityOverview(data);
    } catch (err) {
      console.error('Error fetching commodity overview:', err);
    } finally {
      setLoadingCommodities(false);
    }
  };

  const fetchTopGainers = async (region: string = moverRegion) => {
    setLoadingGainers(true);
    try {
      const { data } = await api.get(`/api/market/top-movers?limit=20&direction=gainers&region=${region}`);
      setTopGainers(data);
    } catch (err) {
      console.error('Error fetching top gainers:', err);
    } finally {
      setLoadingGainers(false);
    }
  };

  const fetchTopLosers = async (region: string = moverRegion) => {
    setLoadingLosers(true);
    try {
      const { data } = await api.get(`/api/market/top-movers?limit=20&direction=losers&region=${region}`);
      setTopLosers(data);
    } catch (err) {
      console.error('Error fetching top losers:', err);
    } finally {
      setLoadingLosers(false);
    }
  };

  useEffect(() => {
    fetchOverview();
    fetchEtfOverview();
    fetchCommodityOverview();
    fetchTopGainers();
    fetchTopLosers();
    const interval = setInterval(() => {
      // Index/ETF/commodity overviews are driven by the live SSE stream now;
      // only the movers (not streamed) still poll here.
      fetchTopGainers();
      fetchTopLosers();
    }, 5 * 60 * 1000);
    return () => clearInterval(interval);
  }, []);

  // A country selection outranks the region it sits in.
  const moverScope = moverCountry || moverRegion;

  useEffect(() => {
    fetchTopGainers(moverScope);
    fetchTopLosers(moverScope);
  }, [moverScope]);

  useEffect(() => {
    let cancelled = false;
    (async () => {
      try {
        const { data } = await api.get('/api/market/mover-scopes');
        if (!cancelled) setMoverScopes(data.regions || []);
      } catch (err) {
        // Non-fatal: the region chips still work, there's just no drill-down.
        console.error('Error fetching mover scopes:', err);
      }
    })();
    return () => { cancelled = true; };
  }, []);

  // Server-driven so a country appears only once it has enough constituents to
  // rank; falls back to the static regions before the first response lands.
  const REGION_OPTIONS = moverScopes.length
    ? moverScopes.map(r => ({ value: r.value, label: r.label }))
    : [
        { value: 'all', label: 'All' },
        { value: 'americas', label: 'Americas' },
        { value: 'europe', label: 'Europe' },
        { value: 'asia', label: 'Asia-Pacific' },
      ];

  const countriesForRegion = moverScopes.find(r => r.value === moverRegion)?.countries ?? [];

  const handleRegionChange = (value: string) => {
    setMoverRegion(value);
    // Drop a country that doesn't belong to the newly picked region, but keep
    // it when it does — switching to 'all' shouldn't discard the drill-down.
    setMoverCountry(prev => {
      if (!prev) return '';
      const next = moverScopes.find(r => r.value === value)?.countries ?? [];
      return next.some(c => c.code === prev) ? prev : '';
    });
  };

  const handleIndexSort = (col: SortCol) => {
    if (indexSortCol === col) {
      if (indexSortDir === 'desc') setIndexSortDir('asc');
      else { setIndexSortCol(null); setIndexSortDir('desc'); }
    } else {
      setIndexSortCol(col);
      setIndexSortDir('desc');
    }
  };

  const handleEtfSort = (col: SortCol) => {
    if (etfSortCol === col) {
      if (etfSortDir === 'desc') setEtfSortDir('asc');
      else { setEtfSortCol(null); setEtfSortDir('desc'); }
    } else {
      setEtfSortCol(col);
      setEtfSortDir('desc');
    }
  };

  const handleCommoditySort = (col: SortCol) => {
    if (commoditySortCol === col) {
      if (commoditySortDir === 'desc') setCommoditySortDir('asc');
      else { setCommoditySortCol(null); setCommoditySortDir('desc'); }
    } else {
      setCommoditySortCol(col);
      setCommoditySortDir('desc');
    }
  };

  const sortedIndexes = React.useMemo(() => {
    const sorted = [...overview];
    if (indexSortCol) {
      sorted.sort((a, b) => {
        if (indexSortCol === 'name') {
          const av = (a.name || '').toLowerCase();
          const bv = (b.name || '').toLowerCase();
          return indexSortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
        }
        const av = (a as any)[indexSortCol] ?? -Infinity;
        const bv = (b as any)[indexSortCol] ?? -Infinity;
        return indexSortDir === 'desc' ? bv - av : av - bv;
      });
    }
    return sorted;
  }, [overview, indexSortCol, indexSortDir]);

  const etfCategories = React.useMemo(() => {
    const cats = new Set(etfOverview.map(e => e.category));
    return ['All', ...Array.from(cats).sort()];
  }, [etfOverview]);

  const filteredEtfs = React.useMemo(() => {
    let items = etfCategory === 'All' ? [...etfOverview] : etfOverview.filter(e => e.category === etfCategory);
    if (etfSortCol) {
      items = [...items].sort((a, b) => {
        if (etfSortCol === 'name') {
          const av = (a.name || '').toLowerCase();
          const bv = (b.name || '').toLowerCase();
          return etfSortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
        }
        const av = (a as any)[etfSortCol] ?? -Infinity;
        const bv = (b as any)[etfSortCol] ?? -Infinity;
        return etfSortDir === 'desc' ? bv - av : av - bv;
      });
    }
    return items;
  }, [etfOverview, etfCategory, etfSortCol, etfSortDir]);

  const commodityCategories = React.useMemo(() => {
    const cats = new Set(commodityOverview.map(c => c.category));
    return ['All', ...Array.from(cats).sort()];
  }, [commodityOverview]);

  const filteredCommodities = React.useMemo(() => {
    let items = commodityCategory === 'All' ? [...commodityOverview] : commodityOverview.filter(c => c.category === commodityCategory);
    if (commoditySortCol) {
      items = [...items].sort((a, b) => {
        if (commoditySortCol === 'name') {
          const av = (a.name || '').toLowerCase();
          const bv = (b.name || '').toLowerCase();
          return commoditySortDir === 'desc' ? bv.localeCompare(av) : av.localeCompare(bv);
        }
        const av = (a as any)[commoditySortCol] ?? -Infinity;
        const bv = (b as any)[commoditySortCol] ?? -Infinity;
        return commoditySortDir === 'desc' ? bv - av : av - bv;
      });
    }
    return items;
  }, [commodityOverview, commodityCategory, commoditySortCol, commoditySortDir]);

  /* ── Compact panel chrome shared by the movers 2×2 grid ── */
  const moverTd: React.CSSProperties = {
    padding: '3px 8px', fontSize: 11, borderBottom: `1px solid ${bloomberg.border.primary}`,
    whiteSpace: 'nowrap', overflow: 'hidden', textOverflow: 'ellipsis',
  };

  const MoverPanel: React.FC<{
    title: string;
    loading: boolean;
    valueLabel: string;                 // 'Price' or 'Volume'
    items: Array<{ symbol: string; company_name?: string; value: string; changePct: number }>;
    direction: 'up' | 'down';
  }> = ({ title, loading, valueLabel, items, direction }) => (
    <Paper elevation={2} sx={{ p: 0, display: 'flex', flexDirection: 'column', maxHeight: 268 }}>
      <Box sx={{ display: 'flex', alignItems: 'center', px: 1.25, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}`, flexShrink: 0 }}>
        <Typography sx={{ fontWeight: 700, fontSize: bloomberg.fontSize.panelTitle, color: bloomberg.text.primary, letterSpacing: '0.02em' }}>{title}</Typography>
      </Box>
      {loading ? (
        <Box sx={{ display: 'flex', flexDirection: 'column', gap: 0.75, p: 1.25 }}>
          {[...Array(6)].map((_, i) => <Skeleton key={i} variant="rounded" height={20} />)}
        </Box>
      ) : items.length === 0 ? (
        <Typography color="text.secondary" variant="body2" sx={{ p: 1.5, fontSize: bloomberg.fontSize.bodyDense }}>No data available</Typography>
      ) : (
        <Box sx={{ overflow: 'auto' }}>
          <table style={{ width: '100%', borderCollapse: 'collapse', tableLayout: 'fixed' }}>
            <colgroup>
              <col style={{ width: '20%' }} /><col style={{ width: '44%' }} />
              <col style={{ width: '18%' }} /><col style={{ width: '18%' }} />
            </colgroup>
            <tbody>
              {items.map((m) => (
                <tr key={m.symbol} onMouseEnter={e => (e.currentTarget.style.backgroundColor = bloomberg.bg.hover)} onMouseLeave={e => (e.currentTarget.style.backgroundColor = 'transparent')}>
                  <td style={moverTd}><TickerLink symbol={m.symbol} fontSize={11} /></td>
                  <td style={{ ...moverTd, color: bloomberg.text.secondary }} title={m.company_name}>{m.company_name || '—'}</td>
                  <td style={{ ...moverTd, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 600, color: bloomberg.text.primary }}>{m.value}</td>
                  <td style={{ ...moverTd, textAlign: 'right', fontFamily: '"IBM Plex Mono", monospace', fontWeight: 700, color: direction === 'up' ? bloomberg.status.positive : bloomberg.status.negative }}>
                    {direction === 'up' ? '+' : ''}{m.changePct.toFixed(direction === 'up' && valueLabel === 'Volume' ? 0 : 2)}%
                  </td>
                </tr>
              ))}
            </tbody>
          </table>
        </Box>
      )}
    </Paper>
  );

  return (
    <Box>

      {/* Header: market-status strip — index minis + sentiment + live state */}
      <Box sx={{
        display: 'flex', alignItems: 'center', gap: { xs: 2, md: 3 }, mb: 3,
        flexWrap: 'wrap', px: 2, py: 1.25,
        bgcolor: bloomberg.bg.secondary, border: `1px solid ${bloomberg.border.primary}`, borderRadius: 1,
      }}>
        <SectorTicker embedded />
        <Box sx={{ ml: { xs: 0, md: 'auto' }, display: 'flex', alignItems: 'center', gap: 1.5, flexShrink: 0 }}>
          <Tooltip title={live.connected
            ? <RefreshCountdown lastUpdated={live.lastUpdated} connected={live.connected} />
            : 'Reconnecting to live stream…'}>
            <Box sx={{
              display: 'inline-flex', alignItems: 'center', gap: 0.5,
              px: 0.75, py: '3px', borderRadius: '4px',
              border: `1px solid ${live.connected ? 'rgba(34,197,94,0.4)' : bloomberg.border.primary}`,
              bgcolor: live.connected ? 'rgba(34,197,94,0.10)' : 'transparent',
            }}>
              <Box className={live.connected ? 'bb-live-dot' : undefined}
                sx={{ width: 6, height: 6, borderRadius: '50%', bgcolor: live.connected ? '#22c55e' : bloomberg.text.muted }} />
              <Typography sx={{ fontSize: 9, fontWeight: 700, letterSpacing: '0.1em', color: live.connected ? '#22c55e' : bloomberg.text.muted }}>
                {live.connected ? 'LIVE' : 'OFFLINE'}
              </Typography>
            </Box>
          </Tooltip>
        </Box>
      </Box>

      <TodaySynthesis overview={overview} />

      {/* Macro regime — same "state of the world" altitude as the synthesis
          line above, and above the dense lookup tables below. Click-through to
          the full macro page. */}
      <MacroRegimeStrip />

      {/* Movers module: region chips + 3-col grid (price gainers/losers, sentiment) —
          promoted above the reference tables since "what's interesting today"
          shouldn't sit below dense lookup data. */}
      <Paper elevation={2} sx={{ p: 0, mb: 3 }}>
        <Box sx={{ display: 'flex', alignItems: 'center', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}`, flexWrap: 'wrap' }}>
          <Typography sx={{ fontWeight: 700, fontSize: 13, color: bloomberg.accent.orange, mr: 1 }}>Market Movers</Typography>
          <Typography variant="caption" sx={{ color: bloomberg.text.muted, fontWeight: 600, textTransform: 'uppercase', letterSpacing: '0.05em' }}>Region:</Typography>
          {REGION_OPTIONS.map(r => (
            <Chip
              key={r.value}
              label={r.label}
              size="small"
              onClick={() => handleRegionChange(r.value)}
              sx={{
                height: 22, fontSize: 10, fontWeight: 600, cursor: 'pointer',
                bgcolor: moverRegion === r.value ? bloomberg.accent.orange : bloomberg.bg.tertiary,
                color: moverRegion === r.value ? bloomberg.text.inverse : bloomberg.text.secondary,
                border: `1px solid ${moverRegion === r.value ? bloomberg.accent.orange : bloomberg.border.primary}`,
                '&:hover': { bgcolor: moverRegion === r.value ? bloomberg.accent.orange : bloomberg.bg.hover },
              }}
            />
          ))}

          {/* Country drill-down. Regions alone bury a German move under 1,900
              US names; this narrows to one market. Only rendered once the
              scopes call returns a country for the selected region. */}
          {countriesForRegion.length > 0 && (
            <Select
              value={moverCountry}
              onChange={(e) => setMoverCountry(e.target.value as string)}
              displayEmpty
              size="small"
              renderValue={(v) =>
                (v as string)
                  ? countriesForRegion.find(c => c.code === v)?.name ?? (v as string)
                  : 'All countries'
              }
              sx={{
                height: 22, ml: 0.5, minWidth: 132,
                fontSize: 10, fontWeight: 600,
                color: moverCountry ? bloomberg.text.inverse : bloomberg.text.secondary,
                bgcolor: moverCountry ? bloomberg.accent.orange : bloomberg.bg.tertiary,
                '& .MuiOutlinedInput-notchedOutline': {
                  borderColor: moverCountry ? bloomberg.accent.orange : bloomberg.border.primary,
                },
                '&:hover .MuiOutlinedInput-notchedOutline': {
                  borderColor: moverCountry ? bloomberg.accent.orange : bloomberg.border.secondary,
                },
                '& .MuiSelect-icon': {
                  color: moverCountry ? bloomberg.text.inverse : bloomberg.text.muted,
                },
                '& .MuiSelect-select': { py: 0, pl: 1 },
              }}
              MenuProps={{
                PaperProps: {
                  sx: {
                    bgcolor: bloomberg.bg.secondary,
                    border: `1px solid ${bloomberg.border.primary}`,
                    maxHeight: 360,
                  },
                },
              }}
            >
              <MenuItem value="" sx={{ fontSize: 11 }}>All countries</MenuItem>
              {/* On the 'all' tab every eligible country is offered at once, so
                  group them by region to keep a 15-item list navigable. */}
              {moverRegion === 'all'
                ? REGION_OPTIONS.filter(r => r.value !== 'all').flatMap(r => {
                    const group = countriesForRegion.filter(c => c.region === r.value);
                    if (!group.length) return [];
                    return [
                      <ListSubheader
                        key={`h-${r.value}`}
                        sx={{
                          bgcolor: bloomberg.bg.tertiary,
                          color: bloomberg.text.muted,
                          fontSize: 9, fontWeight: 700, lineHeight: '20px',
                          textTransform: 'uppercase', letterSpacing: '0.06em',
                        }}
                      >
                        {r.label}
                      </ListSubheader>,
                      ...group.map(c => (
                        <MenuItem key={c.code} value={c.code} sx={{ fontSize: 11 }}>
                          {c.name}
                          <Typography component="span" sx={{ ml: 'auto', pl: 1.5, fontSize: 9, color: bloomberg.text.muted }}>
                            {c.count}
                          </Typography>
                        </MenuItem>
                      )),
                    ];
                  })
                : countriesForRegion.map(c => (
                    <MenuItem key={c.code} value={c.code} sx={{ fontSize: 11 }}>
                      {c.name}
                      <Typography component="span" sx={{ ml: 'auto', pl: 1.5, fontSize: 9, color: bloomberg.text.muted }}>
                        {c.count}
                      </Typography>
                    </MenuItem>
                  ))}
            </Select>
          )}
        </Box>
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', md: '1fr 1fr 1fr' }, gap: 1.5, p: 1.5 }}>
          <MoverPanel
            title="Top Gainers"
            loading={loadingGainers}
            valueLabel="Price"
            direction="up"
            items={topGainers.slice(0, 6).map(m => ({ symbol: m.symbol, company_name: m.company_name, value: m.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), changePct: m.change_percent }))}
          />
          <MoverPanel
            title="Top Losers"
            loading={loadingLosers}
            valueLabel="Price"
            direction="down"
            items={topLosers.slice(0, 6).map(m => ({ symbol: m.symbol, company_name: m.company_name, value: m.price.toLocaleString(undefined, { minimumFractionDigits: 2, maximumFractionDigits: 2 }), changePct: m.change_percent }))}
          />
          <SentimentCard />
        </Box>
      </Paper>

      {/* Market Data: Indexes / ETFs / Commodities — tabbed instead of three
          simultaneous columns, so each table gets full width (easier to read
          the full Ticker/Name/Price/1D/1M/YTD/1Y granularity) at the cost of
          only seeing one at a time. */}
      <Paper elevation={2} sx={{ p: 0, mb: 3 }}>
        <Box sx={{ display: 'flex', gap: 1, px: 1.5, py: 1, borderBottom: `1px solid ${bloomberg.border.primary}` }}>
          {([
            ['indexes', 'Market Indexes'],
            ['etfs', 'ETFs'],
            ['commodities', 'Commodities'],
          ] as const).map(([key, label]) => (
            <Chip
              key={key}
              label={label}
              size="small"
              onClick={() => setMarketTab(key)}
              sx={{
                height: 24, fontSize: 11, fontWeight: 700, cursor: 'pointer',
                bgcolor: marketTab === key ? bloomberg.accent.orange : bloomberg.bg.tertiary,
                color: marketTab === key ? bloomberg.text.inverse : bloomberg.text.secondary,
                border: `1px solid ${marketTab === key ? bloomberg.accent.orange : bloomberg.border.primary}`,
                '&:hover': { bgcolor: marketTab === key ? bloomberg.accent.orange : bloomberg.bg.hover },
              }}
            />
          ))}
        </Box>
        {marketTab === 'indexes' && (
          <SimpleMarketTable
            title="Market Indexes"
            loading={loadingOverview}
            items={sortedIndexes}
            sortCol={indexSortCol}
            sortDir={indexSortDir}
            onSort={handleIndexSort}
            maxHeight={520}
            flashes={live.flashes}
            compact={isMobile}
          />
        )}
        {marketTab === 'etfs' && (
          <SimpleMarketTable
            title="ETFs"
            loading={loadingEtfs}
            items={filteredEtfs}
            sortCol={etfSortCol}
            sortDir={etfSortDir}
            onSort={handleEtfSort}
            maxHeight={520}
            categories={etfCategories}
            selectedCategory={etfCategory}
            onCategoryChange={setEtfCategory}
            flashes={live.flashes}
            compact={isMobile}
          />
        )}
        {marketTab === 'commodities' && (
          <SimpleMarketTable
            title="Commodities"
            loading={loadingCommodities}
            items={filteredCommodities}
            sortCol={commoditySortCol}
            sortDir={commoditySortDir}
            onSort={handleCommoditySort}
            maxHeight={520}
            categories={commodityCategories}
            selectedCategory={commodityCategory}
            onCategoryChange={setCommodityCategory}
            flashes={live.flashes}
            compact={isMobile}
          />
        )}
      </Paper>

      {/* WatchlistStrip temporarily disabled: model portfolios have no owner
          column yet, so the aggregate watched-set mixes in other users' model
          portfolio holdings (e.g. Marc's), which reads as "random names" on
          the dashboard. Re-enable once ownership scoping is fixed. */}
      <SignalsDigest />

      {/* Calendars: Macro | Earnings side by side — dropped entirely on
          mobile. Both are planning tools rather than "quick check" content,
          and neither component's internal tables (minWidth 600-700px) are
          worth redoing for a phone; skipping them also skips their fetches. */}
      {!isMobile && (
        <Box sx={{ display: 'grid', gridTemplateColumns: { xs: '1fr', lg: '1fr 1fr' }, gap: 2, alignItems: 'start' }}>
          <MacroCalendar />
          <UpcomingEarningsCalendar compact />
        </Box>
      )}
    </Box>
  );
};

export default Dashboard;
